import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Car, Gauge, Calendar, X, ChevronLeft, ChevronRight, Phone, Mail, Search, Filter } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, BRAND } from "../App";

const CarsForSalePage = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("available");
  const [inquiryForm, setInquiryForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [showInquiry, setShowInquiry] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchCars(); }, []);

  const fetchCars = async () => {
    try {
      const res = await axios.get(`${API}/cars`);
      setCars(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(price);
  const formatMileage = (m) => new Intl.NumberFormat("en-US").format(m);

  const getCarImages = (car) => {
    if (car.images && car.images.length > 0) return car.images;
    if (car.image_url) return [car.image_url];
    return [];
  };

  const filteredCars = cars.filter(car => {
    const matchesSearch = searchQuery === "" || 
      `${car.year} ${car.make} ${car.model}`.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || car.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openCar = (car) => {
    setSelectedCar(car);
    setCurrentImageIndex(0);
    setShowInquiry(false);
    setInquiryForm({ name: "", email: "", phone: "", message: `I'm interested in the ${car.year} ${car.make} ${car.model}` });
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: inquiryForm.name,
        email: inquiryForm.email,
        phone: inquiryForm.phone,
        budget: formatPrice(selectedCar.price),
        car_preference: `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}`,
        message: inquiryForm.message,
        car_id: selectedCar.id,
      });
      toast.success("Inquiry submitted! We'll be in touch soon.");
      setShowInquiry(false);
      setSelectedCar(null);
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const images = selectedCar ? getCarImages(selectedCar) : [];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-black text-white pt-32 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-4">Vehicle Inventory</p>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight">Cars for Sale</h1>
          <p className="text-white/80 mt-3">Quality pre-owned vehicles at affordable prices.</p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-gray-50 py-6 border-b border-black/10 sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by year, make, or model..."
                className="w-full border border-black/20 p-3 pl-10 focus:border-[#E31E24] focus:outline-none bg-white"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none bg-white font-semibold"
            >
              <option value="all">All Vehicles</option>
              <option value="available">Available</option>
              <option value="pending">Pending</option>
              <option value="sold">Sold</option>
            </select>
          </div>
        </div>
      </section>

      {/* Cars Grid */}
      <section className="py-12 bg-white min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner"></div></div>
          ) : filteredCars.length === 0 ? (
            <div className="text-center py-20">
              <Car size={64} className="mx-auto mb-4 text-black/20" />
              <p className="text-xl text-black/60">No vehicles match your search.</p>
              <p className="text-sm text-black/40 mt-2">Try adjusting your filters or check back soon.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCars.map((car, idx) => (
                <motion.div
                  key={car.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="group bg-gray-50 border border-black/10 overflow-hidden hover:shadow-xl transition-all cursor-pointer"
                  onClick={() => openCar(car)}
                >
                  <div className="aspect-video bg-gray-200 relative overflow-hidden">
                    {car.image_url || (car.images && car.images[0]) ? (
                      <img src={car.image_url || car.images[0]} alt={`${car.year} ${car.make} ${car.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Car size={64} className="text-black/20" /></div>
                    )}
                    {car.status !== "available" && (
                      <div className="absolute top-3 right-3 bg-black text-white px-3 py-1 text-xs font-black uppercase">
                        {car.status}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-black uppercase mb-1 text-black">{car.year} {car.make} {car.model}</h3>
                    {car.trim && <p className="text-sm text-black/60 mb-2">{car.trim}</p>}
                    <p className="text-2xl font-black text-[#E31E24] mb-4">{formatPrice(car.price)}</p>
                    <div className="flex items-center gap-4 text-black/60 text-sm mb-4">
                      <span className="flex items-center gap-1"><Gauge size={16} />{formatMileage(car.mileage)} mi</span>
                      <span className="flex items-center gap-1"><Calendar size={16} />{car.year}</span>
                    </div>
                    <button className="w-full bg-black text-white font-bold uppercase py-3 group-hover:bg-[#E31E24] transition-colors">
                      View Details
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Car Detail Modal */}
      <AnimatePresence>
        {selectedCar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedCar(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white w-full max-w-4xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-gray-100">
                <button onClick={() => setSelectedCar(null)} className="absolute top-4 right-4 z-10 bg-black/60 text-white p-2 hover:bg-black">
                  <X size={24} />
                </button>

                {images.length > 0 ? (
                  <>
                    <img src={images[currentImageIndex]} alt="" className="w-full aspect-video object-cover" />
                    {images.length > 1 && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((i) => (i - 1 + images.length) % images.length); }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 text-white p-3 hover:bg-black">
                          <ChevronLeft size={24} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((i) => (i + 1) % images.length); }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 text-white p-3 hover:bg-black">
                          <ChevronRight size={24} />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center"><Car size={100} className="text-black/20" /></div>
                )}

                {images.length > 1 && (
                  <div className="flex gap-2 p-4 overflow-x-auto bg-gray-200">
                    {images.map((img, idx) => (
                      <button key={idx} onClick={() => setCurrentImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-14 overflow-hidden border-2 ${idx === currentImageIndex ? "border-[#E31E24]" : "border-transparent"}`}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-8">
                <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                  <div>
                    <h2 className="text-3xl font-black uppercase mb-1">{selectedCar.year} {selectedCar.make} {selectedCar.model}</h2>
                    {selectedCar.trim && <p className="text-black/60">{selectedCar.trim}</p>}
                  </div>
                  <p className="text-4xl font-black text-[#E31E24]">{formatPrice(selectedCar.price)}</p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  <div className="bg-gray-50 p-3 border border-black/10">
                    <p className="text-black/40 text-xs uppercase">Year</p>
                    <p className="font-bold">{selectedCar.year}</p>
                  </div>
                  <div className="bg-gray-50 p-3 border border-black/10">
                    <p className="text-black/40 text-xs uppercase">Mileage</p>
                    <p className="font-bold">{formatMileage(selectedCar.mileage)} mi</p>
                  </div>
                  <div className="bg-gray-50 p-3 border border-black/10">
                    <p className="text-black/40 text-xs uppercase">Make</p>
                    <p className="font-bold">{selectedCar.make}</p>
                  </div>
                  <div className="bg-gray-50 p-3 border border-black/10">
                    <p className="text-black/40 text-xs uppercase">Status</p>
                    <p className="font-bold uppercase">{selectedCar.status}</p>
                  </div>
                </div>

                {selectedCar.features && (
                  <div className="mb-4">
                    <p className="text-xs uppercase font-bold text-black/60 mb-2">Features</p>
                    <p className="text-black/80">{selectedCar.features}</p>
                  </div>
                )}

                {selectedCar.description && (
                  <div className="mb-6">
                    <p className="text-xs uppercase font-bold text-black/60 mb-2">Description</p>
                    <p className="text-black/80">{selectedCar.description}</p>
                  </div>
                )}

                {!showInquiry ? (
                  <div className="border-t border-black/10 pt-6 flex flex-col sm:flex-row gap-3">
                    <a href={BRAND.phoneHref} className="flex-1 bg-[#E31E24] text-white font-bold uppercase px-6 py-4 hover:bg-[#C01A1F] text-center flex items-center justify-center gap-2">
                      <Phone size={18} /> Call {BRAND.phone}
                    </a>
                    <button onClick={() => setShowInquiry(true)} className="flex-1 bg-black text-white font-bold uppercase px-6 py-4 hover:bg-black/80">
                      Contact Seller
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleInquirySubmit} className="border-t border-black/10 pt-6">
                    <h3 className="font-black uppercase mb-4">Inquire About This Vehicle</h3>
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      <input type="text" value={inquiryForm.name} onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                        required placeholder="Your Name *" className="border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                      <input type="email" value={inquiryForm.email} onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                        required placeholder="Email *" className="border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                    </div>
                    <input type="tel" value={inquiryForm.phone} onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                      required placeholder="Phone *" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none mb-3" />
                    <textarea value={inquiryForm.message} onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                      rows={3} placeholder="Message" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none mb-4 resize-none" />
                    <div className="flex gap-3">
                      <button type="button" onClick={() => setShowInquiry(false)} className="flex-1 border border-black/20 font-bold uppercase py-3 hover:bg-black hover:text-white">
                        Cancel
                      </button>
                      <button type="submit" disabled={submitting} className="flex-1 bg-[#E31E24] text-white font-bold uppercase py-3 hover:bg-[#C01A1F] disabled:opacity-50">
                        {submitting ? "Sending..." : "Send Inquiry"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CarsForSalePage;
